import { TaskCategory, UserType } from '@enrollhere/classes';

import { AgencyTaskSettings } from '../services/agency-task-settings.service';
import { StringListLike, readStringList } from './task-assignment';

/**
 * Category allow-list for create or view (API / Firestore).
 *
 * Creator semantics:
 * - `null` / `undefined` creatorAccess = closed. Nobody in a restricted role
 *   (AGENT / EMPLOYEE) may create.
 * - `allowAllAgents` — every agent may create (gated by the agency
 *   `agentsCanCreateTasksByDefault` setting).
 * - `allowAllEmployees` — every employee may create (gated by the agency
 *   `employeesCanCreateTasksByDefault` setting).
 * - `userIds` / `permanentTags` — specific people/tags may always create,
 *   regardless of role or agency gate.
 *
 * The `allowAll*` flags are only meaningful for creator access; viewer access
 * uses `userIds` / `permanentTags` only.
 */
export interface TaskCategoryCreatorAccess {
  allowAllAgents?: boolean;
  allowAllEmployees?: boolean;
  userIds: string[];
  permanentTags: string[];
}

/** Alias — same shape for creator and viewer allow-lists. */
export type TaskCategoryAccess = TaskCategoryCreatorAccess;

/** Flat wire shape for category notify audiences (API / editor). */
export interface TaskCategoryNotifyTarget {
  includeAssignees?: boolean;
  userIds?: string[];
  permanentTags?: string[];
}

/**
 * TaskCategory with optional access fields from API.
 * Proto `notifyOnCreate` / `notifyWhenDue` use StringListContainer; the API
 * wire is flat `string[]`. Those fields are omitted here so this type stays
 * assignable to `TaskCategory`. Read/write flat notify via get/set helpers.
 */
export type TaskCategoryWithCreatorAccess = Omit<
  TaskCategory,
  'creatorAccess' | 'viewerAccess' | 'notifyOnCreate' | 'notifyWhenDue'
> & {
  creatorAccess?: TaskCategoryCreatorAccess;
  viewerAccess?: TaskCategoryAccess;
  /** Set on agent/employee list/get responses from the API. */
  canCreateTasks?: boolean;
};

type CategoryWithNotifyWire = TaskCategoryWithCreatorAccess & {
  notifyOnCreate?: TaskCategoryNotifyTarget;
  notifyWhenDue?: TaskCategoryNotifyTarget;
};

/**
 * Work pool on the wire. The API sends flat `string[]` where the proto keeps
 * `StringListContainer`, so read and write it through these helpers rather
 * than off the model type.
 */
type CategoryWithAssignmentWire = {
  defaultAssignedToUserIds?: StringListLike;
  defaultAssignedToTags?: StringListLike;
};

/** Default work pool stamped on every task created in this category. */
export function getCategoryDefaultAssignees(
  category: TaskCategory | TaskCategoryWithCreatorAccess
): TaskCategoryAccess {
  const source = category as unknown as CategoryWithAssignmentWire;
  return {
    userIds: readStringList(source.defaultAssignedToUserIds),
    permanentTags: readStringList(source.defaultAssignedToTags),
  };
}

export function applyCategoryDefaultAssignees(
  category: TaskCategoryWithCreatorAccess,
  assignees: TaskCategoryAccess
): TaskCategoryWithCreatorAccess {
  const next = category as unknown as CategoryWithAssignmentWire;
  next.defaultAssignedToUserIds = assignees.userIds;
  next.defaultAssignedToTags = assignees.permanentTags;
  return category;
}

export function getNotifyOnCreate(
  category: TaskCategoryWithCreatorAccess
): TaskCategoryNotifyTarget | undefined {
  return (category as CategoryWithNotifyWire).notifyOnCreate;
}

export function getNotifyWhenDue(
  category: TaskCategoryWithCreatorAccess
): TaskCategoryNotifyTarget | undefined {
  return (category as CategoryWithNotifyWire).notifyWhenDue;
}

export function applyNotifyTargets(
  category: TaskCategoryWithCreatorAccess,
  targets: {
    notifyOnCreate?: TaskCategoryNotifyTarget;
    notifyWhenDue?: TaskCategoryNotifyTarget;
  }
): TaskCategoryWithCreatorAccess {
  const next = category as CategoryWithNotifyWire;
  if (targets.notifyOnCreate !== undefined) {
    next.notifyOnCreate = targets.notifyOnCreate;
  }
  if (targets.notifyWhenDue !== undefined) {
    next.notifyWhenDue = targets.notifyWhenDue;
  }
  return next;
}

function normalizeCreatorAccess(
  access: Partial<TaskCategoryCreatorAccess>
): TaskCategoryCreatorAccess {
  return {
    allowAllAgents: Boolean(access.allowAllAgents),
    allowAllEmployees: Boolean(access.allowAllEmployees),
    userIds: access.userIds ?? [],
    permanentTags: access.permanentTags ?? [],
  };
}

export function getCreatorAccess(
  category: TaskCategory | TaskCategoryWithCreatorAccess
): TaskCategoryCreatorAccess | undefined {
  const access = (
    category as {
      creatorAccess?: Partial<TaskCategoryCreatorAccess> | null;
    }
  ).creatorAccess;

  if (access == null) {
    return undefined;
  }

  return normalizeCreatorAccess(access);
}

/** Dialer staff (agents and employees) share agency task-create rules. */
export function usesAgencyTaskCreateRestrictions(userType: number): boolean {
  return userType === UserType.AGENT || userType === UserType.EMPLOYEE;
}

export function hasCreatorAccessRestrictions(
  access: TaskCategoryCreatorAccess | undefined
): boolean {
  return access != null;
}

function userMatchesCreatorAllowList(
  access: TaskCategoryCreatorAccess,
  userId: string,
  permanentTags: string[]
): boolean {
  if (userId && access.userIds.includes(userId)) {
    return true;
  }

  if (
    access.permanentTags.length > 0 &&
    permanentTags.some(tag => access.permanentTags.includes(tag))
  ) {
    return true;
  }

  return false;
}

function extractRawAccess(
  raw: Record<string, unknown> | undefined,
  camelKey: 'creatorAccess' | 'viewerAccess',
  snakeKey: 'creator_access' | 'viewer_access'
): Partial<TaskCategoryCreatorAccess> | undefined {
  if (!raw) {
    return undefined;
  }

  const access = (raw[camelKey] ?? raw[snakeKey]) as
    Partial<TaskCategoryCreatorAccess> | null | undefined;

  if (access == null) {
    return undefined;
  }

  return access;
}

/** Read creatorAccess from API / Firestore JSON (camelCase or legacy snake_case). */
export function parseCreatorAccessFromRaw(
  raw: Record<string, unknown> | undefined
): TaskCategoryCreatorAccess | undefined {
  const access = extractRawAccess(raw, 'creatorAccess', 'creator_access');
  if (!access) {
    return undefined;
  }
  return normalizeCreatorAccess(access);
}

/** Read viewerAccess (overseers) from API / Firestore JSON. */
export function parseViewerAccessFromRaw(
  raw: Record<string, unknown> | undefined
): TaskCategoryAccess | undefined {
  const access = extractRawAccess(raw, 'viewerAccess', 'viewer_access');
  if (!access) {
    return undefined;
  }
  return {
    userIds: access.userIds ?? [],
    permanentTags: access.permanentTags ?? [],
  };
}

/**
 * Parse a notify target from API JSON. Accepts flat arrays or
 * StringListContainer `{ list: string[] }` shapes.
 */
export function parseNotifyTargetFromRaw(
  raw: unknown
): TaskCategoryNotifyTarget | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const source = raw as Record<string, unknown>;
  const userIdsRaw = source['userIds'];
  const tagsRaw = source['permanentTags'];
  const userIds = Array.isArray(userIdsRaw)
    ? (userIdsRaw as string[])
    : Array.isArray((userIdsRaw as { list?: string[] } | undefined)?.list)
      ? (userIdsRaw as { list: string[] }).list
      : [];
  const permanentTags = Array.isArray(tagsRaw)
    ? (tagsRaw as string[])
    : Array.isArray((tagsRaw as { list?: string[] } | undefined)?.list)
      ? (tagsRaw as { list: string[] }).list
      : [];
  const hasField =
    source['includeAssignees'] !== undefined ||
    userIds.length > 0 ||
    permanentTags.length > 0;
  if (!hasField) {
    return undefined;
  }
  return {
    includeAssignees: Boolean(source['includeAssignees']),
    ...(userIds.length > 0 ? { userIds } : {}),
    ...(permanentTags.length > 0 ? { permanentTags } : {}),
  };
}

/**
 * Read the category work pool from API / Firestore JSON. The wire is flat
 * `string[]`, Firestore keeps the proto `StringListContainer`.
 */
export function parseDefaultAssigneesFromRaw(
  raw: Record<string, unknown> | undefined
): TaskCategoryAccess | undefined {
  if (!raw) {
    return undefined;
  }
  const userIds = readStringList(
    raw['defaultAssignedToUserIds'] as StringListLike
  );
  const permanentTags = readStringList(
    raw['defaultAssignedToTags'] as StringListLike
  );
  if (
    raw['defaultAssignedToUserIds'] === undefined &&
    raw['defaultAssignedToTags'] === undefined
  ) {
    return undefined;
  }
  return { userIds, permanentTags };
}

/** Map the stored work pool to the category editor picker value. */
export function defaultAssigneesForEditor(
  category: TaskCategory | TaskCategoryWithCreatorAccess | undefined
): TaskCategoryAccess | null {
  if (!category) {
    return null;
  }
  const assignees = getCategoryDefaultAssignees(category);
  if (assignees.userIds.length === 0 && assignees.permanentTags.length === 0) {
    return null;
  }
  return assignees;
}

/** Map stored access to the task category editor form value. */
export function creatorAccessForEditor(
  access: TaskCategoryCreatorAccess | undefined
): TaskCategoryCreatorAccess | null {
  if (!hasCreatorAccessRestrictions(access)) {
    return null;
  }

  return {
    allowAllAgents: Boolean(access?.allowAllAgents),
    allowAllEmployees: Boolean(access?.allowAllEmployees),
    userIds: access?.userIds ?? [],
    permanentTags: access?.permanentTags ?? [],
  };
}

/** Map stored viewerAccess to the category editor form value. */
export function viewerAccessForEditor(
  access: TaskCategoryAccess | undefined
): TaskCategoryAccess | null {
  if (!access) {
    return null;
  }
  // Empty allow-list is the same as "no extra overseers" — tagless managers
  // already see everything; do not persist empty arrays as a restriction.
  if (
    (access.userIds?.length ?? 0) === 0 &&
    (access.permanentTags?.length ?? 0) === 0
  ) {
    return null;
  }
  return {
    userIds: access.userIds ?? [],
    permanentTags: access.permanentTags ?? [],
  };
}

function sortedStrings(values: string[] | undefined): string[] {
  return [...(values ?? [])].sort();
}

function accessMatchesRequest(
  requested: TaskCategoryCreatorAccess | null | undefined,
  persisted: TaskCategoryCreatorAccess | undefined
): boolean {
  if (requested == null) {
    return !hasCreatorAccessRestrictions(persisted);
  }

  if (!hasCreatorAccessRestrictions(persisted) || !persisted) {
    return false;
  }

  return (
    Boolean(requested.allowAllAgents) === Boolean(persisted.allowAllAgents) &&
    Boolean(requested.allowAllEmployees) ===
      Boolean(persisted.allowAllEmployees) &&
    JSON.stringify(sortedStrings(requested.userIds)) ===
      JSON.stringify(sortedStrings(persisted.userIds)) &&
    JSON.stringify(sortedStrings(requested.permanentTags)) ===
      JSON.stringify(sortedStrings(persisted.permanentTags))
  );
}

/** Whether persisted category creatorAccess matches what the editor requested. */
export function creatorAccessMatchesRequest(
  requested: TaskCategoryCreatorAccess | null | undefined,
  category: TaskCategory | null | undefined
): boolean {
  const persisted = category
    ? getCreatorAccess(category as TaskCategoryWithCreatorAccess)
    : undefined;
  return accessMatchesRequest(requested, persisted);
}

export function getViewerAccess(
  category: TaskCategory | TaskCategoryWithCreatorAccess
): TaskCategoryAccess | undefined {
  const access = (
    category as {
      viewerAccess?: Partial<TaskCategoryAccess> | null;
    }
  ).viewerAccess;

  if (access == null) {
    return undefined;
  }

  return {
    userIds: access.userIds ?? [],
    permanentTags: access.permanentTags ?? [],
  };
}

/** Whether persisted category viewerAccess matches what the editor requested. */
export function viewerAccessMatchesRequest(
  requested: TaskCategoryAccess | null | undefined,
  category: TaskCategory | null | undefined
): boolean {
  const persisted = category
    ? getViewerAccess(category as TaskCategoryWithCreatorAccess)
    : undefined;
  return accessMatchesRequest(requested, persisted);
}

/**
 * Whether a user of the given role may create tasks in this category.
 *
 * - creatorAccess null → closed (nobody).
 * - Specific `userIds` / `permanentTags` always allow, regardless of role.
 * - `allowAllAgents` allows any AGENT, but only while the agency
 *   `agentsCanCreateTasksByDefault` gate is on.
 * - `allowAllEmployees` allows any EMPLOYEE, but only while the agency
 *   `employeesCanCreateTasksByDefault` gate is on.
 */
export function isCategoryCreatableByUser(
  category: TaskCategoryWithCreatorAccess,
  userType: number,
  userId: string,
  permanentTags: string[],
  settings?: AgencyTaskSettings
): boolean {
  const access = getCreatorAccess(category);
  if (!access) {
    return false;
  }

  if (userMatchesCreatorAllowList(access, userId, permanentTags)) {
    return true;
  }

  if (userType === UserType.AGENT) {
    return (
      access.allowAllAgents === true &&
      settings?.agentsCanCreateTasksByDefault === true
    );
  }

  if (userType === UserType.EMPLOYEE) {
    return (
      access.allowAllEmployees === true &&
      settings?.employeesCanCreateTasksByDefault === true
    );
  }

  return false;
}

export function permanentTagsForAgency(
  memberships: readonly {
    agencyId: string;
    permission?: { permanentTags?: { list: string[] } };
  }[],
  agencyId: string
): string[] {
  return (
    memberships.find(m => m.agencyId === agencyId)?.permission?.permanentTags
      ?.list ?? []
  );
}

export function canAgentCreateTasks(
  userType: number,
  settings: AgencyTaskSettings | undefined,
  creatableCategoryCount: number | undefined
): boolean {
  if (userType === UserType.UNKNOWN_TYPE) {
    return false;
  }
  if (!usesAgencyTaskCreateRestrictions(userType)) {
    return true;
  }
  if (settings === undefined || creatableCategoryCount === undefined) {
    return false;
  }
  return creatableCategoryCount > 0;
}

export function categoriesExposeCanCreateFlag(
  categories: TaskCategoryWithCreatorAccess[]
): boolean {
  return (
    categories.length > 0 &&
    categories.every(c => typeof c.canCreateTasks === 'boolean')
  );
}

export function countCreatableCategories(
  categories: TaskCategoryWithCreatorAccess[],
  options: {
    userType: number;
    userId: string;
    permanentTags: string[];
    settings: AgencyTaskSettings | undefined;
  }
): number {
  if (categories.some(c => typeof c.canCreateTasks === 'boolean')) {
    return categories.filter(c => c.canCreateTasks === true).length;
  }
  return filterCreatableTaskCategories(categories, options).length;
}

export function hasCreatableCategories(
  categories: TaskCategoryWithCreatorAccess[],
  options: {
    userType: number;
    userId: string;
    permanentTags: string[];
    settings: AgencyTaskSettings | undefined;
  }
): boolean {
  return countCreatableCategories(categories, options) > 0;
}

export function filterCreatableTaskCategories(
  categories: TaskCategoryWithCreatorAccess[],
  options: {
    userType: number;
    userId: string;
    permanentTags: string[];
    settings: AgencyTaskSettings | undefined;
  }
): TaskCategoryWithCreatorAccess[] {
  if (!usesAgencyTaskCreateRestrictions(options.userType)) {
    if (options.userType === UserType.UNKNOWN_TYPE) {
      return [];
    }
    return categories;
  }

  if (options.settings === undefined) {
    return [];
  }

  return categories.filter(category => {
    if (typeof category.canCreateTasks === 'boolean') {
      return category.canCreateTasks;
    }
    return isCategoryCreatableByUser(
      category,
      options.userType,
      options.userId,
      options.permanentTags,
      options.settings
    );
  });
}
